import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as LinkIcon, Search, X, Boxes, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import Modal from "../../components/primitives/Modal";
import TextButton from "../../components/primitives/TextButton";
import { useSkus, loadSkus } from "../../services/skuStore";
import "./SkuPickerModal.css";

function fmtDim(n) {
    if (n == null) return "—";
    return Number(n) % 1 === 0 ? String(Number(n)) : Number(n).toFixed(2);
}

function SkuPickerModal({ open, onClose, onSelect }) {
    const { skus, loading, error } = useSkus();
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        if (open) {
            setQuery("");
            setActiveIdx(0);
            // Make sure cache is fresh-ish — refresh in background if data exists.
            // (loadSkus respects the cache; force=false skips if already loaded.)
            loadSkus().catch(() => {});
        }
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = skus;
        if (q) {
            list = list.filter((s) => {
                const n = (s.name ?? "").toLowerCase();
                const c = (s.sku_code ?? "").toLowerCase();
                return n.includes(q) || c.includes(q);
            });
        }
        return [...list].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );
    }, [skus, query]);

    useEffect(() => {
        setActiveIdx(0);
    }, [query, skus.length]);

    useEffect(() => {
        const node = listRef.current?.querySelector(
            `[data-idx="${activeIdx}"]`
        );
        node?.scrollIntoView({ block: "nearest" });
    }, [activeIdx]);

    const onKeyDown = (e) => {
        if (!filtered.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const picked = filtered[activeIdx];
            if (picked) onSelect?.(picked);
        }
    };

    const isEmpty = !loading && skus.length === 0 && !error;
    const noMatches = skus.length > 0 && filtered.length === 0;

    return (
        <Modal
            open={open}
            onClose={onClose}
            eyebrow="Manifest · pick from catalog"
            title="Insert SKU"
            width={520}
            initialFocus={inputRef}
            footer={
                <div className="picker-foot">
                    <Link
                        to="/catalog"
                        className="picker-foot__manage mono"
                        onClick={onClose}
                    >
                        <ExternalLink size={12} aria-hidden="true" />
                        Manage catalog
                    </Link>
                    <TextButton variant="ghost" onClick={onClose}>
                        Close
                    </TextButton>
                </div>
            }
        >
            <div className="picker-search">
                <Search size={14} className="picker-search__icon" aria-hidden="true" />
                <input
                    ref={inputRef}
                    type="search"
                    className="picker-search__input focus-ring"
                    placeholder="Search by name or SKU code"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    aria-label="Search SKUs"
                    autoComplete="off"
                />
                {query && (
                    <button
                        type="button"
                        className="picker-search__clear"
                        onClick={() => setQuery("")}
                        aria-label="Clear search"
                    >
                        <X size={12} aria-hidden="true" />
                    </button>
                )}
            </div>

            {loading && skus.length === 0 ? (
                <div className="picker-state mono uppercase-label">
                    Loading catalog…
                </div>
            ) : error && skus.length === 0 ? (
                <div className="picker-state picker-state--error">
                    {error.message || "Failed to load catalog"}
                </div>
            ) : isEmpty ? (
                <div className="picker-empty">
                    <Boxes size={20} aria-hidden="true" className="picker-empty__icon" />
                    <div className="picker-empty__title">No saved SKUs yet</div>
                    <p className="picker-empty__lede">
                        Add SKUs in the catalog to use them here.
                    </p>
                    <Link
                        to="/catalog"
                        className="picker-empty__cta mono"
                        onClick={onClose}
                    >
                        <ExternalLink size={12} aria-hidden="true" />
                        Open catalog
                    </Link>
                </div>
            ) : noMatches ? (
                <div className="picker-state mono">
                    No matches for "{query}"
                </div>
            ) : (
                <ul
                    ref={listRef}
                    className="picker-list"
                    role="listbox"
                    aria-label="SKUs"
                >
                    {filtered.map((sku, idx) => (
                        <li
                            key={sku.id}
                            role="option"
                            aria-selected={idx === activeIdx}
                            data-idx={idx}
                            className={`picker-item ${idx === activeIdx ? "is-active" : ""}`}
                            onMouseEnter={() => setActiveIdx(idx)}
                            onClick={() => onSelect?.(sku)}
                        >
                            <div className="picker-item__main">
                                <span className="picker-item__name">{sku.name}</span>
                                {sku.sku_code && (
                                    <span className="picker-item__code mono">
                                        {sku.sku_code}
                                    </span>
                                )}
                            </div>
                            <span className="picker-item__dims mono-num">
                                {fmtDim(sku.width)} × {fmtDim(sku.height)} × {fmtDim(sku.depth)}
                            </span>
                            <LinkIcon
                                size={12}
                                className="picker-item__pick"
                                aria-hidden="true"
                            />
                        </li>
                    ))}
                </ul>
            )}
        </Modal>
    );
}

export default SkuPickerModal;
